<template>
	<v-dialog
		v-if="show"
		v-model="show"
		@keydown.esc="show = false"
		width="400px"
	>
		<v-card>
			<v-card-title class="success white--text">
				{{ actionText }} to:
			</v-card-title>
			<v-card-text>
				<template v-if="!isChangeCategory">
					<v-autocomplete
						v-model="project"
						:items="projects"
						label="Project"
						item-text="name"
						return-object
						persistent-hint
						@input="loadReportsAndCategories"
					/>
					<v-autocomplete
						v-model="report"
						:items="reports"
						:disabled="loadingReports"
						:loading="loadingReports"
						:filter="autocompleteFilter"
						label="Report"
						item-text="displayName"
						return-object
						persistent-hint
					>

						<template
							v-for="slotName in ['selection', 'item']"
							:slot="slotName"
							slot-scope="data"
						>
							<div :key="slotName">
								{{ data.item.displayName }}
								<span
									v-if="data.item.date"
									class="caption"
								>
									- {{ data.item.date }}
								</span>
								<span
									v-if="data.item.id === currentReport.id"
									class="caption"
								>
									(active)
								</span>
							</div>
						</template>
					</v-autocomplete>
				</template>
				<v-autocomplete
					v-model="category"
					:items="categories"
					:disabled="loadingCategories"
					:loading="loadingCategories"
					:filter="autocompleteFilter"
					label="Category"
					return-object
					persistent-hint
				/>
			</v-card-text>
			<v-divider/>
			<v-card-actions>
				<v-spacer/>
				<v-btn
					:disabled="!report && !isChangeCategory"
					flat
					color="success"
					@click="doAction()"
				>
					{{ actionText }}
				</v-btn>
				<v-btn
					flat
					color="error"
					@click="show = false"
				>
					close
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { ActiveReports } from '@/models/Report';
import { ActiveCategories } from '@/models/Category';
import { Articles } from '@/models/Article';
import { sync } from 'vuex-pathify';

export default {
	data() {
		return {
			actions: [
				{ key: 'move', text: 'Move' },
				{ key: 'copy', text: 'Copy' },
				{ key: 'change-category', text: 'Change Category' },
			],
			project: null, // Selected project
			report: null, // Selected report
			category: 0, // Selected category

			reports: [], // All reports for chosen project
			categories: [], // All categories for chosen project

			loadingReports: false,
			loadingCategories: false,

			// Cache already loaded reports and categories to prevent refetching them from backend
			// @todo find better way to work with cache
			cachedReports: [],
			cachedCategories: [],
		};
	},
	computed: {
		show: sync('articlesBulkDialog/show'),
		action: sync('articlesBulkDialog/action'),
		actionText() {
			return this.actions.find(action => action.key === this.action).text;
		},
		articles: sync('articlesBulkDialog/articles'),
		isChangeCategory() {
			return (this.action === 'change-category');
		},
		currentProject() {
			return this.$store.get('projects/currentProject');
		},
		projects() {
			return this.$store.get('projects/active').models;
		},
		currentReport() {
			return this.$store.get('reports/currentReport');
		},
		initialReports() {
			let reports = this.$store.get('reports/all').models;

			// Exclude current report from dropdown when action is 'move' and current project chosen
			if (this.action === 'move') {
				reports = reports.filter(report => report.id !== this.currentReport.id);
			}

			return reports.filter(report => !report.isArchived);
		},
	},
	watch: {
		show(isOpen) {
			isOpen ? this.onOpen() : this.onClose();
		},
	},
	methods: {
		// Set initial project and reports, store's currentProject and reports should be loaded by this
		onOpen() {
			if (this.project === null) {
				this.project = this.currentProject;
			}
			this.loadReportsAndCategories();
		},
		onClose() {
			// Set back to default (unselect)
			this.project = null;
			this.report = null;
			this.category = 0;
		},
		sortReports(reports) {
			return reports.sort((a, b) => a.displayName.localeCompare(b.displayName));
		},
		autocompleteFilter(item, queryText, itemText) {
			const contains = itemText.toLowerCase().indexOf(queryText.toLowerCase());
			return (contains > -1);
		},
		loadReportsAndCategories() {
			this.loadReports();
			this.loadCategories();
		},
		loadReports() {
			// Use initial reports for current project if it's selected
			if (this.project === this.currentProject) {
				this.reports = this.sortReports(this.initialReports);
			}
			// Or fetch reports for different project
			else {
				// Check if reports already loaded in cache
				const cache = this.cachedReports.find(item => item.projectId === this.project.id);

				if (cache) {
					this.reports = cache.reports;
					return;
				}

				this.loadingReports = true;

				const reports = new ActiveReports();
				reports.set('id', this.project.id);
				reports.fetch()
					.then(() => {
						this.reports = this.sortReports(reports.models);
						// Add loaded reports to cache
						this.cachedReports.push({ projectId: this.project.id, reports: this.reports });
					})
					.catch((e) => {
						dump('Error fetching active reports', e);
						this.$notify('error', 'Failed to get Reports');
					})
					.finally(() => {
						this.loadingReports = false;
					});
			}
		},
		loadCategories() {
			// Check if categories already loaded in cache
			const cache = this.cachedCategories.find(item => item.projectId === this.project.id);

			if (cache) {
				this.categories = cache.categories;
				return;
			}

			this.loadingCategories = true;

			const categoriesCollection = new ActiveCategories();
			categoriesCollection.set('projectId', this.project.id);
			categoriesCollection.fetch().then(() => {
				let categories = categoriesCollection.models.map((o) => {
					return {
						text: o.name,
						value: o.id,
						level: o.level,
					}
				});

				categories.forEach((cat) => {
					let dashAmount = cat.level;
					while (dashAmount > 1) {
						if (dashAmount === cat.level) {
							cat.label = `\u2014 ${cat.label}`;
						}
						else {
							cat.label = `\u2014${cat.label}`;
						}
						dashAmount--;
					}
				});

				// Default keep original
				categories.unshift({ text: '-- Original Category --', value: 0, level: 0 });

				this.categories = categories;

				// Add loaded categories to cache
				this.cachedCategories.push({ projectId: this.project.id, categories: this.categories });
			}).catch((e) => {
				dump('Error fetching active categories', e);
				this.$notify('error', 'Failed to get categories.');
			}).finally(() => {
				this.loadingCategories = false;
			});
		},
		doAction() {
			const articles = new Articles();
			articles.add(this.articles);

			switch (this.action) {
			case 'move':
				articles.move(this.report, this.category.value);
				break;
			case 'copy':
				articles.copy(this.report, this.category.value);
				break;
			case 'change-category':
				articles.changeCategory(this.category.value);
				break;
			case 'refetch-stats':
				articles.refetchStats();
				break;
			default:
				console.log('no bulk action?');
			}

			this.show = false;
		},
	},
};
</script>
